/**
 * BulkAllBatchesPage Component
 *
 * Page displaying all batches with pagination and ability to view records
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Table, type TableColumn, SearchBar, Button, Tag, DrawerProvider, Loader } from 'equal-ds-ui';
import { FileText, Eye, RefreshCw, Download, ArrowLeft, X } from 'lucide-react';
import { bulkMisAPI } from '../../../services/bulk-mis-api.service';
import { useBulkMisData } from './hooks/useBulkMisData';
import { useBatchRecords } from './hooks/useBatchRecords';
import { BulkBatchRecordsDrawer } from './BulkBatchRecordsDrawer';
import { Alert } from '../../ui/Alert';
import { Select } from '../../ui/Select';
import { Toast } from '../../ui/Toast';
import type { BulkMisFilters, BulkBatchRecord, BulkMisPageProps } from '../../../types/bulk-mis';
import { getProcessingTime } from '../../../utils/time-format';

export const BulkAllBatchesPage: React.FC<BulkMisPageProps> = () => {
  // State for batches table
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState<BulkMisFilters>({
    search: '',
    status: 'all',
    dateRange: { from: null, to: null },
    pipelineSource: 'all',
    batchType: 'all',
  });

  // State for records drawer
  const [recordsDrawerOpen, setRecordsDrawerOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<BulkBatchRecord | null>(null);
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsPageSize, setRecordsPageSize] = useState(25);
  const [recordsStatusFilter, setRecordsStatusFilter] = useState('all');
  const [recordsSearch, setRecordsSearch] = useState('');
  // Always fetch with include_balances since drawer shows both tabs internally
  const includeBalances = true;

  // State for export
  const [isExporting, setIsExporting] = useState(false);

  // State for toast
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');
  const hideSuccessMetrics = sessionStorage.getItem('bulkMisHideSuccessMetrics') === 'true';

  // Toast handlers
  const showToast = useCallback((message: string, variant: 'success' | 'error' = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
  }, []);

  const closeToast = useCallback(() => {
    setToastMessage(null);
  }, []);

  // Fetch batches data
  const { data: batchesData, loading: batchesLoading, error, refresh } = useBulkMisData(filters, page, pageSize);

  // Fetch records for selected batch (useEffect in hook handles fetching when referenceId changes)
  const {
    data: recordsData,
    loading: recordsLoading,
  } = useBatchRecords(
    selectedBatch?.reference_id || '',
    recordsPage,
    recordsPageSize,
    recordsStatusFilter,
    recordsSearch,
    includeBalances
  );

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Get status badge
  const getStatusBadge = (status: BulkBatchRecord['status']) => {
    const statusConfig = {
      COMPLETED: { label: 'Completed', className: 'bg-green-100 text-green-800' },
      PROCESSING: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-800' },
      FAILED: { label: 'Failed', className: 'bg-red-100 text-red-800' },
      UPLOADED: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-800' },
      IN_PROGRESS: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-800' },
      READY: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-800' },
    };

    const config = statusConfig[status] || statusConfig.IN_PROGRESS;

    return (
      <Tag className={config.className} size="sm">
        {config.label}
      </Tag>
    );
  };

  // Check if batch is in progress (not completed or failed)
  const isInProgress = (status: BulkBatchRecord['status']) => {
    return status === 'UPLOADED' || status === 'PROCESSING' || status === 'IN_PROGRESS' || status === 'READY';
  };

  // Handle view records - open drawer
  // Note: fetchRecords() is NOT called here because the useBatchRecords hook
  // already has a useEffect that triggers fetch when referenceId changes.
  // Calling it here would cause duplicate API calls.
  const handleViewRecords = (batch: BulkBatchRecord) => {
    setSelectedBatch(batch);
    setRecordsPage(1);
    setRecordsStatusFilter('all');
    setRecordsSearch('');
    setRecordsDrawerOpen(true);
  };

  // Handle close records drawer
  const handleCloseRecordsDrawer = () => {
    setRecordsDrawerOpen(false);
    setSelectedBatch(null);
  };

  // Handle search
  const handleSearch = (value: string) => {
    setFilters({ ...filters, search: value });
    setPage(1);
  };

  // Handle status filter
  const handleStatusFilter = (value: string) => {
    setFilters({ ...filters, status: value });
    setPage(1);
  };

  // Handle pipeline source filter
  const handlePipelineSourceFilter = (value: string) => {
    setFilters({ ...filters, pipelineSource: value });
    setPage(1);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      dateRange: { from: null, to: null },
      pipelineSource: 'all',
      batchType: 'all',
    });
    setPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters =
    filters.search ||
    filters.status !== 'all' ||
    filters.pipelineSource !== 'all' ||
    filters.dateRange.from ||
    filters.dateRange.to;

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  // Handle export
  const handleExport = async () => {
    try {
      setIsExporting(true);

      // Prepare export params from current filters
      const exportParams = {
        status: filters.status,
        pipeline_source: filters.pipelineSource,
        search: filters.search,
        from_date: filters.dateRange?.from ? filters.dateRange.from.toISOString().split('T')[0] : undefined,
        to_date: filters.dateRange?.to ? filters.dateRange.to.toISOString().split('T')[0] : undefined,
      };

      const blob = await bulkMisAPI.exportData(exportParams);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bulk-mis-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showToast('Data exported successfully', 'success');
    } catch (error) {
      console.error('[BulkAllBatchesPage] Export failed:', error);
      showToast('Failed to export data. Please try again.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle records search
  const handleRecordsSearch = (value: string) => {
    setRecordsSearch(value);
    setRecordsPage(1);
  };

  // Handle records status filter
  const handleRecordsStatusFilter = (value: string) => {
    setRecordsStatusFilter(value);
    setRecordsPage(1);
  };

  // Define batch columns
  const columns: TableColumn<BulkBatchRecord>[] = useMemo(
    () => {
      const baseColumns: TableColumn<BulkBatchRecord>[] = [
        {
          key: 'reference_id',
          header: 'Reference ID',
          width: 180,
          accessor: (row: BulkBatchRecord) => row.reference_id,
          render: (_value: string, row: BulkBatchRecord) => (
            <span className="font-mono text-sm text-gray-900" title={row.reference_id || 'N/A'}>
              {row.reference_id
                ? (row.reference_id.length > 20 ? `${row.reference_id.substring(0, 20)}...` : row.reference_id)
                : 'N/A'}
            </span>
          ),
        },
        {
          key: 'file_name',
          header: 'File Name',
          width: 200,
          accessor: (row: BulkBatchRecord) => row.file_name,
          render: (_value: string, row: BulkBatchRecord) => (
            <span className="text-sm text-gray-900" title={row.file_name}>
              {row.file_name.length > 30
                ? `${row.file_name.substring(0, 30)}...`
                : row.file_name}
            </span>
          ),
        },
        {
          key: 'pipeline_source',
          header: 'Source',
          width: 100,
          accessor: (row: BulkBatchRecord) => row.pipeline_source,
          render: (value: string) => (
            <span className="text-sm text-gray-700 capitalize">{value}</span>
          ),
        },
        {
          key: 'status',
          header: 'Status',
          width: 120,
          accessor: (row: BulkBatchRecord) => row.status,
          render: (_value: string, row: BulkBatchRecord) => getStatusBadge(row.status),
        },
        {
          key: 'records',
          header: 'Records',
          width: 150,
          accessor: (row: BulkBatchRecord) => row.total_records,
          render: (_value: number, row: BulkBatchRecord) => (
            <div className="text-sm">
              <div className="font-medium text-gray-900">Total: {row.total_records}</div>
              {!hideSuccessMetrics && !isInProgress(row.status) && (
                <div className="text-black text-xs">
                  Success: {row.successful_records ?? 0} | Failed: {row.failed_records ?? 0}
                </div>
              )}
            </div>
          ),
        },
      ];

      if (!hideSuccessMetrics) {
        baseColumns.push({
          key: 'success_rate',
          header: 'Success Rate',
          width: 120,
          accessor: (row: BulkBatchRecord) => row.success_rate ?? 0,
          render: (_value: number, row: BulkBatchRecord) => {
            if (isInProgress(row.status)) {
              return <span className="text-sm text-gray-400">-</span>;
            }
            const rate = row.success_rate ?? 0;
            const color =
              rate >= 90 ? 'text-green-600' : rate >= 70 ? 'text-yellow-600' : 'text-red-600';
            return <span className={`font-medium ${color}`}>{rate.toFixed(1)}%</span>;
          },
        });
      }

      baseColumns.push(
        {
          key: 'processing_time',
          header: 'Processing Time',
          width: 140,
          accessor: (row: BulkBatchRecord) => row.created_at,
          render: (_value: string, row: BulkBatchRecord) => {
            if (isInProgress(row.status)) {
              return <span className="text-sm text-gray-400">-</span>;
            }
            return <span className="text-sm text-gray-700">{getProcessingTime(row)}</span>;
          },
        },
        {
          key: 'created_at',
          header: 'Created At',
          width: 160,
          accessor: (row: BulkBatchRecord) => row.created_at,
          render: (_value: string, row: BulkBatchRecord) => (
            <span className="text-sm text-gray-700">{formatDate(row.created_at)}</span>
          ),
        },
        {
          key: 'actions',
          header: 'Actions',
          isActions: true,
          width: 120,
          align: 'center',
          accessor: (row: BulkBatchRecord) => row.batch_id || row.reference_id,
          render: (_value: string, row: BulkBatchRecord) => (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewRecords(row);
              }}
              className="inline-flex items-center justify-center p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              title="View Records"
              aria-label="View Records"
            >
              <Eye className="w-4 h-4" />
            </button>
          ),
        },
      );

      return baseColumns;
    },
    [hideSuccessMetrics]
  );

  // Handle back navigation
  const handleBack = () => {
    // Use Angular bridge for navigation if available
    if ((window as any).AngularAuthBridge?.navigate) {
      (window as any).AngularAuthBridge.navigate('/dashboard/bulkDataFetch');
    } else {
      // Fallback to hash-based navigation
      window.location.hash = '#/dashboard/bulkDataFetch';
    }
  };

  return (
    <div className="equal-ds-root p-6 bg-gray-50 min-h-screen">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <Toast message={toastMessage} onClose={closeToast} variant={toastVariant} />
        </div>
      )}

      {/* Back Button */}
      <button
        onClick={handleBack}
        className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Bulk Data Fetch
      </button>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">All Batches</h1>
            <p className="text-gray-600 mt-2">View and manage all bulk operation batches</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={refresh}
              disabled={batchesLoading}
              variant="secondary"
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${batchesLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleExport}
              disabled={isExporting || !batchesData || batchesData.batches.length === 0}
              variant="secondary"
              className="flex items-center gap-2"
            >
              <Download className={`w-4 h-4 ${isExporting ? 'animate-bounce' : ''}`} />
              {isExporting ? 'Exporting...' : 'Export Data'}
            </Button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="error" className="mb-6">
          {error}
        </Alert>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-row gap-4 items-center">
          {/* Search */}
          <div className="flex-1">
            <SearchBar
              placeholder="Search by file name, batch ID..."
              onSearch={handleSearch}
              className="w-full"
            />
          </div>

          {/* Status Filter */}
          <div className="w-48">
            <Select
              value={filters.status}
              onChange={handleStatusFilter}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'IN_PROGRESS', label: 'In Progress' },
                { value: 'COMPLETED', label: 'Completed' },
                { value: 'FAILED', label: 'Failed' },
              ]}
            />
          </div>

          {/* Pipeline Source Filter */}
          <div className="w-48">
            <Select
              value={filters.pipelineSource}
              onChange={handlePipelineSourceFilter}
              options={[
                { value: 'all', label: 'All Sources' },
                { value: 'api', label: 'API' },
                { value: 'portal', label: 'Portal' },
              ]}
            />
          </div>

          {/* Reset Button (only if filters are active) */}
          {hasActiveFilters && (
            <Button
              onClick={handleResetFilters}
              variant="secondary"
              className="flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* Batches Table */}
      {batchesLoading && !batchesData ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <Loader cellSize={12} />
            <p className="text-gray-600">Loading batches...</p>
          </div>
        </div>
      ) : batchesData && batchesData.batches.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12">
          <div className="text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No batches found</p>
            <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
          </div>
        </div>
      ) : batchesData ? (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Batches</h2>
            <p className="text-sm text-gray-700 mt-1">
              Showing {batchesData.batches.length} of {batchesData.pagination.total_records} batches
            </p>
          </div>

          <Table
            data={batchesData.batches}
            columns={columns}
            loading={batchesLoading}
            onRowClick={handleViewRecords}
            pagination={{
              currentPage: batchesData.pagination.page,
              pageSize: batchesData.pagination.limit,
              totalItems: batchesData.pagination.total_records,
              totalPages: batchesData.pagination.total_pages,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
              pageSizeOptions: [10, 25, 50, 100],
            }}
          />
        </div>
      ) : null}

      {/* Records Drawer */}
      <DrawerProvider open={recordsDrawerOpen} onOpenChange={setRecordsDrawerOpen} side="right">
        <BulkBatchRecordsDrawer
          isOpen={recordsDrawerOpen}
          onClose={handleCloseRecordsDrawer}
          onExportError={(message) => showToast(message, 'error')}
          batch={selectedBatch}
          records={recordsData?.records || []}
          pagination={recordsData?.pagination || { page: 1, limit: 25, total_records: 0, total_pages: 0, has_next: false, has_prev: false }}
          loading={recordsLoading}
          summary={recordsData?.summary || undefined}
          onPageChange={setRecordsPage}
          onPageSizeChange={setRecordsPageSize}
          onStatusFilterChange={handleRecordsStatusFilter}
          onSearchChange={handleRecordsSearch}
        />
      </DrawerProvider>
    </div>
  );
};
